import { clientFetch } from '~/lib/.client/fetch';
import type { PlanLevel } from '~/lib/.client/plans';
import { requestWithAuth } from '~/lib/.client/request-with-auth';
import type { TokenStats } from '~/lib/.server/rateLimit';
import { trackTeamMemberInvited, trackTeamMemberUpgradeRequested } from '~/lib/analytics/team-membership';
import { CLIENT_ORIGIN } from '~/lib/constants';
import { getMessageFromApiError } from '~/lib/error-handling';
import { SHOW_TEAM_SETTINGS_PARAM, SUBSCRIPTION_UPDATE_SEARCH_PARAM } from '~/lib/stores/pricing/config';
import { userStore } from '~/lib/stores/user';
import type { TeamPromptGetResponse } from '~/routes/api.organizations.($provider).($id).team-prompt';
import type {
  OrganizationData,
  OrganizationMembership,
  OrganizationSettings,
  OrganizationUserRoles,
  CustomRegistry,
} from '~/types/organization';
import type { Subscription, SubscriptionInterval } from '~/types/subscription';
import type { EffectiveTier } from '~/utils/effectiveTier';
import { invariant } from '~/utils/invariant';

const requestOrganizationWithAuth = requestWithAuth(CLIENT_ORIGIN);

export async function getOrganizationSubscriptionInfo({ organization }: { organization: OrganizationData }) {
  const response = await requestOrganizationWithAuth<{ billing: { subscription?: Subscription } }>(
    `api/organizations/${organization.provider}/${organization.slug}`,
    undefined,
    'GET',
  );

  invariant(response?.billing?.subscription);

  return response?.billing?.subscription;
}

export async function getOrganizationMembers({
  organization,
}: {
  organization: Pick<OrganizationData, 'provider' | 'slug'>;
}) {
  const response = await requestOrganizationWithAuth<{ members: OrganizationMembership[] }>(
    `api/organizations/${organization.provider}/${organization.slug}/members`,
    undefined,
    'GET',
  );

  invariant(response?.members);

  return response.members;
}

export async function createOrganization({
  slug,
  setupState,
}: {
  slug: string;
  setupState: {
    level: PlanLevel;
    interval: SubscriptionInterval;
  };
}): Promise<{ organization: OrganizationData } | { error: string }> {
  try {
    const response = await requestOrganizationWithAuth<{ organization: OrganizationData | null }>(
      `api/organizations`,
      { organization: { slug, name: slug.trim(), setupState } },
      'POST',
      {
        422: async (response) => {
          const data = await response.json();
          throw { response: { data } };
        },
      },
    );

    invariant(response?.organization);

    return { organization: response.organization };
  } catch (error: any) {
    return { error: getMessageFromApiError(error.response?.data, 'Failed to create team') };
  }
}

export async function deleteOrganization({ org }: { org: OrganizationData }) {
  const response = await requestOrganizationWithAuth<{ organization: OrganizationData | null }>(
    `/api/organizations/${org.provider}/${org.owner}?force_destroy=true`,
    undefined,
    'DELETE',
  );

  invariant(response?.organization);

  return response.organization;
}

export async function editOrganization({
  org,
  data,
}: {
  org: OrganizationData;
  data: Partial<Pick<OrganizationData, 'name'>>;
}) {
  try {
    const response = await requestOrganizationWithAuth<{ organization: OrganizationData | null }>(
      `/api/organizations/${org.provider}/${org.owner}`,
      { organization: data },
      'PATCH',
    );

    if (!response?.organization) {
      throw new Error('Failed to update team name');
    }

    return response.organization;
  } catch (error) {
    throw new Error((error as Error).message || 'Failed to update team name');
  }
}

export async function getOrganizationMembersTokens({
  ids,
  organization,
  tier,
}: {
  ids: string[];
  organization: OrganizationData;
  tier: EffectiveTier;
}): Promise<Record<number, TokenStats>> {
  try {
    const response = await clientFetch(`/api/organizations/${organization.id}/member-tokens`, {
      method: 'POST',
      body: JSON.stringify({ ids, tier }),
    });

    const json = (await response.json()) as { data: Record<number, TokenStats> };

    return json.data;
  } catch (error) {
    throw new Error((error as Error).message || 'Failed to get member tokens');
  }
}

export async function updateMember({
  organization,
  member,
}: {
  organization: OrganizationData;
  member: {
    id: number;
    role: OrganizationUserRoles;
  };
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      members?: OrganizationMembership[];
    }>(`api/organizations/${organization.provider}/${organization.slug}/members/${member.id}`, { member }, 'PATCH');

    return response;
  } catch (error: any) {
    throw new Error(error && 'message' in error ? error.message : 'Failed to update the member');
  }
}

export async function batchUpdateMembers({
  organization,
  members,
}: {
  organization: OrganizationData;
  members: {
    id: number;
    role: OrganizationUserRoles;
    withMembership: boolean;
  }[];
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      members?: OrganizationMembership[];
    }>(
      `api/organizations/${organization.provider}/${organization.slug}/members`,
      {
        members,
      },
      'PATCH',
    );

    return response;
  } catch (error: any) {
    throw new Error(
      error && 'message' in error ? error.message : `Failed to update the member${members.length > 1 ? 's' : ''}`,
    );
  }
}

export async function deleteMember({
  organization,
  memberId,
}: {
  organization: Pick<OrganizationData, 'provider' | 'slug'>;
  memberId: number;
}) {
  try {
    return await requestOrganizationWithAuth(
      `api/organizations/${organization.provider}/${organization.slug}/members/${memberId}`,
      undefined,
      'DELETE',
    );
  } catch (error: any) {
    throw new Error(error.message ? error.message : 'Failed to delete the member');
  }
}

export async function batchDeleteMembers({
  organization,
  members,
}: {
  organization: OrganizationData;
  members: {
    id: number;
  }[];
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      members?: OrganizationMembership[];
    }>(
      `api/organizations/${organization.provider}/${organization.slug}/members`,
      {
        members,
      },
      'DELETE',
    );

    userStore.setActiveOrganization(organization, false);

    return response;
  } catch (error: any) {
    throw new Error(
      error && 'message' in error ? error.message : `Failed to delete the member${members.length > 1 ? 's' : ''}`,
    );
  }
}

export async function resendInvitation({
  organization,
  memberId,
}: {
  organization: Pick<OrganizationData, 'provider' | 'slug'>;
  memberId: number;
}) {
  try {
    return await requestOrganizationWithAuth(
      `api/organizations/${organization.provider}/${organization.slug}/members/${memberId}/resend_invite`,
      undefined,
      'POST',
    );
  } catch (error: any) {
    throw new Error(error.message ? error.message : 'Failed to resend the invitation');
  }
}

export function acceptInvitation({ token }: { token: string }) {
  return requestOrganizationWithAuth(`api/organizations/invites/${token}/accept`, undefined, 'POST').catch((error) => {
    throw new Error(error.message ? error.message : 'Failed to accept the invitation');
  });
}

export function declineInvitation({ token }: { token: string }) {
  return requestOrganizationWithAuth(`api/organizations/invites/${token}/decline`, undefined, 'POST').catch((error) => {
    throw new Error(error.message ? error.message : 'Failed to decline the invitation');
  });
}

export async function batchResendInvitations({
  organization,
  members,
}: {
  organization: OrganizationData;
  members: {
    id: number;
  }[];
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      members?: OrganizationMembership[];
    }>(
      `api/organizations/${organization.provider}/${organization.slug}/members/resend_invites`,
      {
        members,
      },
      'POST',
    );

    return response;
  } catch (error: any) {
    throw new Error(
      error && 'message' in error ? error.message : `Failed to resend the invitation${members.length > 1 ? 's' : ''}`,
    );
  }
}

export async function sendInvites({
  organization,
  members,
}: {
  organization: OrganizationData;
  members: {
    email?: string;
    username?: string;
    role: OrganizationUserRoles;
  }[];
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      members?: OrganizationMembership[];
    }>(`api/organizations/${organization.provider}/${organization.slug}/members`, { members }, 'POST');

    // track successful invitations
    const currentUser = userStore.getUser();
    trackTeamMemberInvited(organization, members, currentUser);

    return response;
  } catch (error: any) {
    throw new Error(error.message ? error.message : 'Failed to send the invite');
  }
}

export async function requestUpgrade({
  organization,
  member,
}: {
  organization: OrganizationData;
  member: {
    id: number;
    email: string;
    username: string;
  };
}) {
  try {
    const response = await requestOrganizationWithAuth(
      `api/organizations/${organization.provider}/${organization.slug}/membership_change_requests`,
      { request: { ...member, with_membership: true } },
      'POST',
    );

    // track successful upgrade request
    const currentUser = userStore.getUser();
    trackTeamMemberUpgradeRequested(organization, member, currentUser);

    return response;
  } catch (error: any) {
    throw new Error(error instanceof Error ? error.message : 'Failed to request the upgrade');
  }
}

export async function getTeamSettings({ organization }: { organization: Pick<OrganizationData, 'provider' | 'slug'> }) {
  try {
    const response = await requestOrganizationWithAuth<{ organizationSettings: OrganizationSettings }>(
      `api/organizations/${organization.provider}/${organization.slug}`,
      undefined,
      'GET',
    );

    invariant(response?.organizationSettings);

    return response.organizationSettings;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch team settings';
    throw new Error(errorMessage);
  }
}

export async function updateTeamSettings({
  organization,
  settings,
}: {
  organization: Pick<OrganizationData, 'provider' | 'slug'>;
  settings: Partial<OrganizationSettings>;
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      settings: OrganizationSettings;
    }>(`api/organizations/${organization.provider}/${organization.slug}/settings`, { settings }, 'PATCH');

    return response?.settings;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update team settings';
    throw new Error(errorMessage);
  }
}

export async function getTeamPrompt({ organization }: { organization: Pick<OrganizationData, 'provider' | 'id'> }) {
  try {
    const response = await clientFetch(`/api/organizations/${organization.provider}/${organization.id}/team-prompt`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch team prompt: ${response.statusText}`);
    }

    const data = await response.json<TeamPromptGetResponse>();

    return data.teamPrompt || null;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'There was an error trying to obtain the team prompt.';
    throw new Error(errorMessage);
  }
}

export async function updateTeamPrompt({
  organization,
  prompt,
}: {
  organization: Pick<OrganizationData, 'provider' | 'id'>;
  prompt: string;
}) {
  try {
    const response = await clientFetch(`/api/organizations/${organization.provider}/${organization.id}/team-prompt`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update team prompt`);
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update team prompt';
    throw new Error(errorMessage);
  }
}

export async function createSubscription({
  organization,
  level,
  interval,
  invitees,
}: {
  organization: OrganizationData;
  level: PlanLevel;
  interval: SubscriptionInterval;
  invitees: string[];
}) {
  try {
    const returnTo = window.origin;

    const successUrl = new URL(returnTo);
    successUrl.searchParams.set(SHOW_TEAM_SETTINGS_PARAM, organization.slug);
    successUrl.searchParams.set(SUBSCRIPTION_UPDATE_SEARCH_PARAM, 'true');

    const params = returnTo
      ? `cancel_url=${returnTo}&redirect_to=${successUrl ? encodeURIComponent(successUrl.toString()) : returnTo}`
      : `cancel_path=${encodeURIComponent(window.location.pathname + window.location.search)}`;

    const response = await requestOrganizationWithAuth<{
      sessionUrl: string;
    }>(
      `/api/organizations/${organization.provider}/${organization.slug}/subscription?${params}`,
      { level, interval, invitees },
      'POST',
    );

    return response;
  } catch (error: any) {
    throw new Error(error.message ? error.message : 'Failed to proceed to checkout');
  }
}

export async function updateSubscription({
  organization,
  level,
  interval,
  invitees,
}: {
  organization: OrganizationData;
  level: PlanLevel;
  interval: SubscriptionInterval;
  invitees: string[];
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      organization: OrganizationData;
    }>(
      `/api/organizations/${organization.provider}/${organization.slug}/subscription`,
      { level, interval, invitees },
      'PUT',
    );

    if (response?.organization) {
      userStore.setActiveOrganization(response.organization, false);
    }

    return response;
  } catch (error: any) {
    throw new Error(error.message ? error.message : 'Failed to update subscription');
  }
}

export async function createCustomRegistry({
  organization,
  patch,
}: {
  organization: Pick<OrganizationData, 'provider' | 'slug'>;
  patch: Partial<Pick<CustomRegistry, 'url' | 'scopes' | 'token'>>;
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      customRegistry: CustomRegistry;
      defaultCustomRegistry: CustomRegistry;
    }>(
      `api/organizations/${organization.provider}/${organization.slug}/custom_registries`,
      { customRegistry: patch },
      'POST',
    );

    return response;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to create custom registry');
  }
}

export async function updateCustomRegistry({
  organization,
  id,
  patch,
}: {
  organization: Pick<OrganizationData, 'provider' | 'slug'>;
  id: string;
  patch: Partial<Pick<CustomRegistry, 'url' | 'scopes' | 'token'>>;
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      customRegistry: CustomRegistry;
    }>(
      `api/organizations/${organization.provider}/${organization.slug}/custom_registries/${id}`,
      { customRegistry: patch },
      'PATCH',
    );

    return response;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to update custom registry');
  }
}

export async function deleteCustomRegistry({
  organization,
  id,
}: {
  organization: Pick<OrganizationData, 'provider' | 'slug'>;
  id: string;
}) {
  try {
    const response = await requestOrganizationWithAuth<{
      defaultCustomRegistry: CustomRegistry;
    }>(`api/organizations/${organization.provider}/${organization.slug}/custom_registries/${id}`, undefined, 'DELETE');

    if (!response) {
      throw new Error('No response received');
    }

    return {
      id,
      defaultCustomRegistry: response.defaultCustomRegistry,
    };
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to delete custom registry');
  }
}

export async function getPackagePrompts({ organization }: { organization: Pick<OrganizationData, 'provider' | 'id'> }) {
  try {
    const response = await clientFetch(
      `/api/organizations/${organization.provider}/${organization.id}/package-prompts`,
      {
        method: 'GET',
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch package prompts: ${response.statusText}`);
    }

    const data = (await response.json()) as { packagePrompts: Array<{ packageName: string; prompt: string }> };

    return data.packagePrompts;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch package prompts';
    throw new Error(errorMessage);
  }
}

export async function updatePackagePrompt({
  organization,
  packageName,
  prompt,
}: {
  organization: Pick<OrganizationData, 'provider' | 'id'>;
  packageName: string;
  prompt: string;
}) {
  try {
    const response = await clientFetch(
      `/api/organizations/${organization.provider}/${organization.id}/package-prompts`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ packageName, prompt }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update package prompt: ${response.statusText}`);
    }

    return true;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update package prompt';
    throw new Error(errorMessage);
  }
}

export async function deletePackagePrompt({
  organization,
  packageName,
}: {
  organization: Pick<OrganizationData, 'provider' | 'id'>;
  packageName: string;
}) {
  try {
    const response = await clientFetch(
      `/api/organizations/${organization.provider}/${organization.id}/package-prompts`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ packageName }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to delete package prompt: ${response.statusText}`);
    }

    return true;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete package prompt';
    throw new Error(errorMessage);
  }
}
